// ============================================================================
// Athlete selection storage + native handover.
// Standalone (imports nothing from the contexts) so both AthleteContext and
// AuthContext can use it without a circular import.
// ============================================================================

export const ATHLETE_STORAGE_KEY = 'beoflow_selected_athlete'

export function loadPersistedSelection(): { profileId: number | null; name: string | null } {
  try {
    const raw = localStorage.getItem(ATHLETE_STORAGE_KEY)
    if (!raw) return { profileId: null, name: null }
    const parsed = JSON.parse(raw)
    return { profileId: parsed.profileId ?? null, name: parsed.name ?? null }
  } catch {
    return { profileId: null, name: null }
  }
}

export function persistSelection(profileId: number | null, name: string | null) {
  if (profileId === null) {
    localStorage.removeItem(ATHLETE_STORAGE_KEY)
  } else {
    localStorage.setItem(ATHLETE_STORAGE_KEY, JSON.stringify({ profileId, name }))
  }
}

/**
 * Seed the athlete selection from URL params handed over by the iOS native Lens
 * header (`athleteId`, `athleteName`; athleteId=0 means "Myself"). Native owns the
 * selection inside the webview, so this overrides any persisted value. Must run
 * before AthleteProvider initializes (called from AuthProvider's boot path), and
 * strips the params from the URL like the auth token. No-op when no param present
 * (plain browser /lens keeps its own selection + in-page selector).
 */
export function seedSelectedAthleteFromUrl() {
  try {
    const url = new URL(window.location.href)
    if (!url.searchParams.has('athleteId')) return
    const idRaw = url.searchParams.get('athleteId')
    const name = url.searchParams.get('athleteName')
    url.searchParams.delete('athleteId')
    url.searchParams.delete('athleteName')
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`)
    const parsed = idRaw && idRaw !== '0' ? Number(idRaw) : null
    const id = Number.isFinite(parsed as number) ? parsed : null
    persistSelection(id, name)
  } catch {
    // Malformed URL / storage unavailable — leave the persisted selection as-is.
  }
}
